import { Quote } from "lucide-react";

const PAIN_POINTS = [
  {
    code: "01",
    title: "The Intent–Action Gap",
    body: "Walking into a store or opening a browser to support women, but leaving with legacy corporate choices because ownership data isn't visible at the shelf or checkout.",
  },
  {
    code: "02",
    title: "The 'Pink-Washing' Trap",
    body: "Realizing a brand's or firm's 'empowerment' marketing is just a facade for a company with zero actual female equity or ownership.",
  },
  {
    code: "03",
    title: "The Sourcing Friction",
    body: "Spending hours digging through fragmented directories trying to find verified, female-led tech, green, or medical services when you need to hire a provider today.",
  },
];

export default function ProblemSection() {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="relative border-b border-line bg-white paper-grain"
    >
      <div className="w-full max-w-none px-4 sm:px-8 lg:px-12 py-20 sm:py-28 lg:py-32">
        {/* Section label */}
        <div className="flex items-center justify-between mb-14">
          <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink-soft">
            01 / The Problem
          </span>
          <span className="hidden sm:block font-mono text-[11px] uppercase tracking-[0.28em] text-ink-soft">
            Heard in every focus group
          </span>
        </div>

        {/* Lead quote */}
        <figure
          data-testid="problem-quote"
          className="max-w-5xl mb-20 lg:mb-24"
        >
          <Quote
            className="w-10 h-10 text-brand-orange mb-6"
            strokeWidth={1.25}
          />
          <blockquote className="font-display font-bold tracking-[-0.01em] text-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
            &ldquo;I&apos;m always looking for women-owned brands. When
            I&apos;m in the store, there is really{" "}
            <span className="text-brand-orange">no easy way to tell</span>.
            With SheScan&trade; I&apos;ve already found two women-owned
            products for my weekly shop!&rdquo;
          </blockquote>
          <figcaption className="mt-8 font-mono text-[11px] uppercase tracking-[0.28em] text-ink-soft">
            — Linda, Chicago
          </figcaption>
        </figure>

        {/* Pain points header */}
        <div className="border-t border-line pt-8 mb-10 flex items-center justify-between">
          <h2
            data-testid="problem-headline"
            className="font-display font-bold tracking-tight text-ink text-2xl sm:text-3xl lg:text-4xl leading-tight"
          >
            Three reasons it doesn't happen.
          </h2>
          <span className="hidden md:block font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
            What we're solving
          </span>
        </div>

        {/* Pain points grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 border-t border-line">
          {PAIN_POINTS.map((p, i) => (
            <div
              key={p.code}
              data-testid={`pain-point-${p.code}`}
              className={`group p-6 sm:p-8 border-b md:border-b-0 border-line ${
                i < PAIN_POINTS.length - 1 ? "md:border-r" : ""
              } hover:border-link transition-colors duration-200`}
            >
              <div className="flex items-center justify-between mb-8">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft group-hover:text-ink-soft">
                  {p.code}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-orange">
                  Pain point
                </span>
              </div>
              <h3 className="font-display font-bold uppercase text-2xl sm:text-3xl tracking-tight text-ink group-hover:text-ink leading-tight">
                {p.title}
              </h3>
              <p className="mt-4 text-base text-ink-soft group-hover:text-ink-soft leading-relaxed">
                {p.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
